import get from 'lodash.get';
import includes from 'lodash.includes';
import isEmpty from 'lodash.isempty';
import first from 'lodash.first';
import last from 'lodash.last';
import compact from 'lodash.compact';
import m from 'moment';

import {
  CHART_TYPE_TIMESERIES,
  CURRENCY_NATIVE,
  DEFAULT_EXCHANGE,
  DEFAULT_LOG_TICK_INTERVAL,
  DEFAULT_MINER,
  FORMATTER_NUMBER,
  FORMAT_CSV,
  FORMAT_JSON,
  RESOLUTION_10MIN,
  RESOLUTION_1H,
  RESOLUTION_1M,
  RESOLUTION_1W,
  RESOLUTION_24H,
  RESOLUTION_MAX_RANGE_10MIN,
  SCALE_LIN,
  SCALE_LOG,
  TIER_2,
  MOVING_AVERAGE,
  MOVING_MEDIAN,
} from '../../constants/app';
import {
  MOVING_MATH_DAYS_OPTIONS,
  MOVING_MATH_HOURS_OPTIONS,
  MOVING_MATH_MINUTES_OPTIONS,
  MOVING_MATH_WEEKS_OPTIONS,
  MOVING_MATH_MONTHS_OPTIONS,
  MOVING_MATH_MINUTES_VALID_RANGE,
  MOVING_MATH_VALID_RANGE,
} from '../../constants/shared';

import { selectUserPlans } from '../auth/user/selectors';
import { selectAssets, selectEndpointsData, selectTopAssets } from '../endpoints/selectors';
import * as routerSelectors from '../router/selectors';

import {
  applyMovingMath,
  createDeepEqualSelector,
  denominateData,
  getMetricConfig,
  normalizeData,
  getErrTitle,
  affixForExchange,
  affixForMiner,
  affixForMovingMath,
  affixForCurrency,
  getResolutionUnit,
} from '../utils/selectors';
import { isMetricLocked } from '../../utils/other';
import { convertDurationToRange } from '../../utils/time';

const isScaleValid = (v) => v === SCALE_LIN || v === SCALE_LOG;

const selectMetrics = (state) => state.metrics;

export const selectMetricsAsset = createDeepEqualSelector(selectMetrics, (metrics) => metrics.asset);

export const selectComputedAsset = createDeepEqualSelector(
  [routerSelectors.selectAsset, selectMetricsAsset],
  (assetInUrl, asset) => assetInUrl || asset,
);

export const selectMetricsCode = createDeepEqualSelector(selectMetrics, (metrics) => metrics.metricCode);

export const selectComputedMetricCode = createDeepEqualSelector(
  [routerSelectors.selectMetricCode, selectMetricsCode],
  (metricCodeInURL, metricCode) => metricCodeInURL || metricCode,
);

export const selectMetricConfig = createDeepEqualSelector(selectComputedMetricCode, (metricCode) =>
  getMetricConfig(metricCode),
);

const makeSelectMetricConfig = (key, defaultValue = undefined) =>
  createDeepEqualSelector(selectMetricConfig, (metricConfig) => get(metricConfig, key, defaultValue));

export const selectMetricAvailableScales = makeSelectMetricConfig('scales');

export const selectMetricDefaultScale = makeSelectMetricConfig('defaultScale');

export const selectMetricDefaultPriceScale = makeSelectMetricConfig('defaultPriceScale');

export const selectMetricResolutionOption = makeSelectMetricConfig('resolutionOption');

export const selectMetricExchangeOption = makeSelectMetricConfig('exchangeOption');

export const selectMetricMinerOption = makeSelectMetricConfig('minerOption');

export const selectMetricMovingAverageOption = makeSelectMetricConfig('movingAverageOption');

export const selectMetricMovingMedianOption = makeSelectMetricConfig('movingMedianOption');

export const selectMetricDatePickerOption = makeSelectMetricConfig('datePickerOption');

export const selectMetricCurrencyOption = makeSelectMetricConfig('currencyOption');

export const selectMetricUpgradeOption = makeSelectMetricConfig('upgradeOption');

export const selectMetricScaleOption = makeSelectMetricConfig('scaleOption');

export const selectMetricCompareOption = makeSelectMetricConfig('compare');

export const selectMetricTier = makeSelectMetricConfig('tier');

export const selectMetricIsNew = makeSelectMetricConfig('isNew');

export const selectMetricPath = makeSelectMetricConfig('path');

export const selectMetricChartType = makeSelectMetricConfig('chartType');

export const selectMetricDescription = makeSelectMetricConfig('description');

export const selectMetricFullName = makeSelectMetricConfig('name');

export const selectMetricShortName = makeSelectMetricConfig('shortName');

export const selectMetricResolutionRestricted = makeSelectMetricConfig('resolutionRestricted');

export const selectMetricDefaultResolution = makeSelectMetricConfig('defaultResolution');

export const selectMetricDefaultCurrency = makeSelectMetricConfig('defaultCurrency');

export const selectMetricUseDenominations = makeSelectMetricConfig('useDenominations');

export const selectMetricStartTs = makeSelectMetricConfig('start');

export const selectMetricOmitInitialDays = makeSelectMetricConfig('omitInitialDays');

export const selectMetricSameAxis = makeSelectMetricConfig('sameAxis');

export const selectMetricTickInterval = makeSelectMetricConfig('logTickInterval', DEFAULT_LOG_TICK_INTERVAL);

export const selectFormatter = makeSelectMetricConfig('formatter', FORMATTER_NUMBER);

const makeSelectComputedParam = (urlSelector, storeSelector, defaultValue) =>
  createDeepEqualSelector([urlSelector, storeSelector], (urlValue, storeValue) => {
    if (urlValue) {
      return urlValue;
    }

    if (storeValue) {
      return storeValue;
    }

    return defaultValue;
  });

export const selectComputedCurrency = makeSelectComputedParam(
  routerSelectors.selectCurrency,
  selectMetricDefaultCurrency,
  CURRENCY_NATIVE,
);
export const selectComputedResolution = makeSelectComputedParam(
  routerSelectors.selectResolution,
  selectMetricDefaultResolution,
  RESOLUTION_24H,
);

export const selectAssetName = createDeepEqualSelector([selectAssets, selectComputedAsset], (assets, asset) => {
  const assetData = assets.find((v) => v.symbol === asset);

  return get(assetData, 'name', asset);
});

export const selectIsTimeSeries = createDeepEqualSelector(
  [selectMetricChartType],
  (chartType = CHART_TYPE_TIMESERIES) => chartType === CHART_TYPE_TIMESERIES,
);

export const selectIsTopAsset = createDeepEqualSelector([selectTopAssets, selectComputedAsset], (topAssets, asset) =>
  topAssets.some((item) => item.symbol === asset),
);

export const selectMetricResolutions = createDeepEqualSelector(
  [selectMetricPath, selectEndpointsData],
  (path, endpointsData = {}) => get(endpointsData, `${path}.resolutions`, []),
);

export const selectMetricFormats = createDeepEqualSelector(
  [selectMetricPath, selectEndpointsData],
  (path, endpointsData = {}) => get(endpointsData, `${path}.formats`, []),
);

export const selectPriceScale = createDeepEqualSelector(
  [routerSelectors.selectPriceScale, selectMetricDefaultPriceScale, selectIsTopAsset],
  (priceScaleInUrl, defaultPriceScale, isTopAsset) => {
    if (isScaleValid(priceScaleInUrl)) {
      return priceScaleInUrl;
    }

    if (defaultPriceScale) {
      return defaultPriceScale;
    }

    if (isTopAsset) {
      return SCALE_LOG;
    }

    return SCALE_LIN;
  },
);

export const selectMetricScale = createDeepEqualSelector(
  [routerSelectors.selectMetricScale, selectMetricAvailableScales, selectMetricDefaultScale],
  (metricScaleInUrl, availableScales = [], defaultScale) => {
    if (isScaleValid(metricScaleInUrl)) {
      return metricScaleInUrl;
    }

    if (availableScales.length === 1) {
      return first(availableScales);
    }

    return defaultScale;
  },
);

export const selectMetricStart = createDeepEqualSelector([selectMetricStartTs, selectComputedAsset], (startTs, asset) =>
  get(startTs, asset, []),
);

export const selectCurrentAssets = createDeepEqualSelector([selectEndpointsData, selectMetricPath], (data, path) => {
  const currentAssets = get(data, `${path}.assets`, []);

  return currentAssets;
});

export const selectIsExchangeValid = createDeepEqualSelector(
  [routerSelectors.selectExchange, selectCurrentAssets, selectComputedAsset],
  (exchangeInUrl, currentAssets = [], asset) => {
    const assetData = currentAssets.find((a) => a.symbol === asset);
    const exchanges = get(assetData, 'exchanges', []);
    const isValid = exchanges.includes(exchangeInUrl);

    return isValid;
  },
);

export const selectComputedExchange = createDeepEqualSelector(
  [routerSelectors.selectExchange, selectIsExchangeValid],
  (exchangeInUrl, isExchangeValid) => {
    if (isExchangeValid) {
      return exchangeInUrl;
    }

    return DEFAULT_EXCHANGE;
  },
);

export const selectComputedMiner = createDeepEqualSelector([routerSelectors.selectMiner], (minerInUrl) => {
  if (minerInUrl) {
    return minerInUrl;
  }

  return DEFAULT_MINER;
});

export const selectCustomDenomination = createDeepEqualSelector(
  [selectMetricUseDenominations, selectComputedAsset],
  (metricDenominations, asset) => get(metricDenominations, asset, {}),
);

export const selectCurrencyLabel = createDeepEqualSelector(
  [selectComputedAsset, selectCustomDenomination],
  (asset, denomination) => get(denomination, 'name', asset),
);

export const selectCurrentAssetsSymbols = createDeepEqualSelector([selectCurrentAssets], (currentAssets) => {
  const assetSymbols = currentAssets.map((asset) => asset.symbol);

  return assetSymbols;
});

export const selectIsMetricDisabled = createDeepEqualSelector(
  [selectComputedAsset, selectCurrentAssetsSymbols],
  (asset, currentMetricAssets) => !includes(currentMetricAssets, asset),
);

export const selectIsMetricLocked = createDeepEqualSelector(
  [selectMetricTier, selectMetricIsNew, selectUserPlans],
  (tier, isNew, userPlans) => {
    if (isNew) {
      return false;
    }

    return isMetricLocked(tier, userPlans);
  },
);

export const selectMetricStart24h = createDeepEqualSelector(selectMetricStart, (metricStart) => first(metricStart));

export const selectMetricStart1h = createDeepEqualSelector(selectMetricStart, (metricStart) => last(metricStart));

export const selectMetricStart10min = () => {
  const [since] = convertDurationToRange(RESOLUTION_MAX_RANGE_10MIN);

  return since;
};

export const selectIsResolutionAvailable = createDeepEqualSelector(
  [selectMetricResolutions, selectComputedResolution, selectMetricResolutionOption],
  (metricResolutions = [], resolution, resolutionOption) => {
    if (isEmpty(metricResolutions)) {
      return false;
    }

    if (!resolutionOption) {
      return false;
    }

    if (metricResolutions.includes(resolution)) {
      return true;
    }

    return false;
  },
);

export const selectIsResolutionAllowed = createDeepEqualSelector(
  [selectUserPlans, selectMetricTier, selectComputedResolution, selectMetricResolutionRestricted],
  (userPlans, metricTier, resolution, resolutionRestricted) => {
    const { isUserAdvanced, isUserProfessional } = userPlans;

    if (!resolutionRestricted) {
      return true;
    }

    if (resolution === RESOLUTION_1M) {
      return true;
    }

    if (resolution === RESOLUTION_1W) {
      return true;
    }

    if (resolution === RESOLUTION_24H) {
      return true;
    }

    if (resolution === RESOLUTION_1H) {
      if (isUserProfessional) {
        return true;
      }

      if (isUserAdvanced && metricTier < TIER_2) {
        return true;
      }
    }

    if (resolution === RESOLUTION_10MIN) {
      if (isUserProfessional) {
        return true;
      }
    }

    return false;
  },
);

export const selectIsResolutionValid = createDeepEqualSelector(
  [selectIsResolutionAvailable, selectIsResolutionAllowed],
  (isResolutionAvailable, isResolutionAllowed) => {
    if (isResolutionAvailable && isResolutionAllowed) {
      return true;
    }

    return false;
  },
);

export const selectValidatedResolution = createDeepEqualSelector(
  [selectIsResolutionValid, selectComputedResolution],
  (isResolutionValid, resolution) => {
    if (isResolutionValid) {
      return resolution;
    }

    return RESOLUTION_24H;
  },
);

export const makeIsMetricFormatAvailable = (format) =>
  createDeepEqualSelector([selectMetricFormats], (metricFormats = []) => {
    if (isEmpty(metricFormats)) {
      return false;
    }

    if (metricFormats.includes(format)) {
      return true;
    }

    return false;
  });

export const selectIsMetricJsonAvailable = makeIsMetricFormatAvailable(FORMAT_JSON);
export const selectIsMetricCsvAvailable = makeIsMetricFormatAvailable(FORMAT_CSV);

export const selectMetricsData = createDeepEqualSelector(
  [selectMetrics, selectMetricOmitInitialDays],
  (metrics, omitInitialDays = 0) => {
    const data = get(metrics, 'metrics.data', []);

    if (!Array.isArray(data)) {
      return [data];
    }

    if (data === null) {
      return [];
    }

    if (omitInitialDays >= data.length) {
      return data;
    }

    return data.slice(omitInitialDays);
  },
);

export const selectMetricsIsEmpty = createDeepEqualSelector([selectMetricsData], (metricsData) => isEmpty(metricsData));

export const selectMetricsLoading = createDeepEqualSelector(selectMetrics, (metrics) => metrics.metrics.loading);

export const selectMetricsErr = createDeepEqualSelector([selectMetrics], (metrics) => metrics.metrics.err);

export const selectMetricsErrMsg = createDeepEqualSelector([selectMetrics], (metrics) => metrics.metrics.errMsg);

export const selectMetricsErrTitle = createDeepEqualSelector(
  [selectMetricsErrMsg, selectUserPlans],
  (errMsg, userPlans) => {
    const { isUserProfessional } = userPlans;

    return getErrTitle(errMsg, isUserProfessional);
  },
);

export const selectNormalizedMetricsData = createDeepEqualSelector([selectMetricsData], normalizeData);

const selectMathedMetricsData = createDeepEqualSelector(
  [
    selectNormalizedMetricsData,
    selectValidatedResolution,
    routerSelectors.selectMovingAverage,
    routerSelectors.selectMovingMedian,
  ],
  (data, resolution, movingAverage, movingMedian) => {
    if (resolution === RESOLUTION_10MIN) {
      const ma = Math.round(movingAverage / 10);
      const mm = Math.round(movingMedian / 10);

      const maRes = applyMovingMath(data, ma, MOVING_AVERAGE);
      const mmRes = applyMovingMath(maRes, mm, MOVING_MEDIAN);

      return mmRes;
    }

    const maRes = applyMovingMath(data, movingAverage, MOVING_AVERAGE);
    const mmRes = applyMovingMath(maRes, movingMedian, MOVING_MEDIAN);

    return mmRes;
  },
);

const selectDenominatedMetricsData = createDeepEqualSelector(
  [selectMathedMetricsData, selectComputedCurrency, selectCustomDenomination],
  denominateData,
);

export const selectFormattedMetricsData = selectDenominatedMetricsData;

export const selectMetricsDataInRange = createDeepEqualSelector(
  [selectFormattedMetricsData, routerSelectors.selectSince, routerSelectors.selectUntil],
  (metricsData, since, until) => {
    if (!since || !until) {
      return metricsData;
    }

    const currentRangeData = metricsData.filter((item) => {
      const time = get(item, '0', 0) / 1000;

      if (time < since || time > until) {
        return false;
      }

      return true;
    });

    return currentRangeData;
  },
);

export const selectIsMetricsDataRangeEmpty = createDeepEqualSelector([selectMetricsDataInRange], (metricsData) =>
  isEmpty(metricsData),
);

export const selectMetricsDataByDate = createDeepEqualSelector(
  [selectNormalizedMetricsData, routerSelectors.selectDate],
  (metricsData = [], date) => {
    if (!date) {
      return last(metricsData) || [];
    }

    const selectedDate = m.unix(date);
    const point = metricsData.find((item) => {
      const t = get(item, '0', 0);
      const d = m(t);

      return d.isSame(selectedDate, 'day');
    });

    if (isEmpty(point)) {
      return [];
    }

    return point;
  },
);

export const selectIsMetricsDataByDateEmpty = createDeepEqualSelector([selectMetricsDataByDate], (metricsData) =>
  isEmpty(metricsData),
);

export const selectMetricsDataStartDate = createDeepEqualSelector([selectNormalizedMetricsData], (metricsData) => {
  const ts = get(first(metricsData), '0', 0);

  return ts;
});

export const selectMetricsDataEndDate = createDeepEqualSelector([selectNormalizedMetricsData], (metricsData) => {
  const ts = get(last(metricsData), '0', 0);

  return ts;
});

export const selectPricesLoading = createDeepEqualSelector(selectMetrics, (metrics) => metrics.prices.loading);

export const selectPricesData = createDeepEqualSelector(
  [selectMetrics, selectMetricsData, selectMetricOmitInitialDays],
  (metrics, metricsData, omitInitialDays = 0) => {
    const data = get(metrics, 'prices.data', []);

    if (isEmpty(data)) {
      return [];
    }

    if (isEmpty(metricsData)) {
      return data.slice(omitInitialDays);
    }

    const metricsStart = get(first(metricsData), 't');

    const result = data.filter((val) => {
      const time = val.t;
      if (time < metricsStart) {
        return false;
      }

      return true;
    });

    return result;
  },
);

const selectNormalizedPriceData = createDeepEqualSelector([selectPricesData], normalizeData);

export const selectFormattedPricesData = selectNormalizedPriceData;

export const selectPricesDataInRange = createDeepEqualSelector(
  [selectFormattedPricesData, routerSelectors.selectSince, routerSelectors.selectUntil],
  (pricesData, since, until) => {
    if (!since || !until) {
      return pricesData;
    }

    const currentRangeData = pricesData.filter((item) => {
      const time = get(item, '0', 0) / 1000;

      if (time < since || time > until) {
        return false;
      }

      return true;
    });

    return currentRangeData;
  },
);

export const selectIsPricesDataRangeEmpty = createDeepEqualSelector([selectPricesDataInRange], (pricesData = []) =>
  isEmpty(pricesData),
);

export const selectHasPriceMinimumPoints = createDeepEqualSelector(
  [selectPricesDataInRange],
  (pricesData = []) => pricesData.length > 1,
);

export const selectDependencyData = createDeepEqualSelector([selectMetrics], (metrics) => {
  const data = get(metrics, 'dependency.data', []);

  return data;
});

export const selectDependencyDataIsLoading = createDeepEqualSelector(
  selectMetrics,
  (metrics) => metrics.dependency.loading,
);

export const selectDependencyDataErr = createDeepEqualSelector([selectMetrics], (metrics) => metrics.dependency.err);

export const selectDependencyDataErrMsg = createDeepEqualSelector(
  [selectMetrics],
  (metrics) => metrics.dependency.errMsg,
);

export const selectDownloadLoadings = createDeepEqualSelector(selectMetrics, (metrics) => metrics.downloadLoadings);

export const selectBaseTitle = createDeepEqualSelector(
  [selectAssetName, selectMetricFullName],
  (assetName, fullName) => {
    return `${assetName}: ${fullName}`;
  },
);

export const selectExchangeAffix = createDeepEqualSelector(
  [selectMetricExchangeOption, selectComputedExchange],
  affixForExchange,
);

export const selectMinerAffix = createDeepEqualSelector([selectMetricMinerOption, selectComputedMiner], affixForMiner);

export const selectMovingAverageAffix = createDeepEqualSelector(
  [selectMetricMovingAverageOption, routerSelectors.selectMovingAverage, selectValidatedResolution],
  (isActive, movingAverage, resolution) => affixForMovingMath(isActive, movingAverage, resolution, 'Moving Average'),
);

export const selectMovingMedianAffix = createDeepEqualSelector(
  [selectMetricMovingMedianOption, routerSelectors.selectMovingMedian, selectValidatedResolution],
  (isActive, movingMedian, resolution) => affixForMovingMath(isActive, movingMedian, resolution, 'Moving Median'),
);

export const selectCurrencyAffix = createDeepEqualSelector(
  [selectMetricCurrencyOption, selectComputedCurrency, selectCurrencyLabel, selectFormatter],
  affixForCurrency,
);

export const selectChartTitle = createDeepEqualSelector(
  [selectBaseTitle, selectExchangeAffix, selectMinerAffix, selectMovingAverageAffix, selectMovingMedianAffix],
  (baseTitle, exchangeAffix, minerAffix, movingAverageAffix, movingMedianAffix) => {
    return compact([baseTitle, exchangeAffix, minerAffix, movingAverageAffix, movingMedianAffix]).join(' ');
  },
);

export const selectMovingMathOptions = createDeepEqualSelector(selectValidatedResolution, (resolution) => {
  if (resolution === RESOLUTION_10MIN) {
    return MOVING_MATH_MINUTES_OPTIONS;
  }

  if (resolution === RESOLUTION_1H) {
    return MOVING_MATH_HOURS_OPTIONS;
  }

  if (resolution === RESOLUTION_1W) {
    return MOVING_MATH_WEEKS_OPTIONS;
  }

  if (resolution === RESOLUTION_1M) {
    return MOVING_MATH_MONTHS_OPTIONS;
  }

  return MOVING_MATH_DAYS_OPTIONS;
});

export const selectMovingMathValidRange = createDeepEqualSelector(selectValidatedResolution, (resolution) => {
  if (resolution === RESOLUTION_10MIN) {
    return MOVING_MATH_MINUTES_VALID_RANGE;
  }

  return MOVING_MATH_VALID_RANGE;
});

export const selectMovingMathUnit = createDeepEqualSelector(selectValidatedResolution, getResolutionUnit);
